import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import WorkflowActionsPanel from '../../components/automations/WorkflowActionsPanel';
import WorkflowBuilderBodyLayout from '../../components/automations/WorkflowBuilderBodyLayout';
import WorkflowBuilderMainChain from '../../components/automations/WorkflowBuilderMainChain';
import WorkflowBuilderStepList from '../../components/automations/WorkflowBuilderStepList';
import WorkflowCanvasViewport from '../../components/automations/WorkflowCanvasViewport';
import WorkflowConditionBranchCanvas from '../../components/automations/WorkflowConditionBranchCanvas';
import WorkflowEditorPanelShell from '../../components/automations/WorkflowEditorPanelShell';
import WorkflowStepCard from '../../components/automations/WorkflowStepCard';
import WorkflowTriggerCanvasSection from '../../components/automations/WorkflowTriggerCanvasSection';
import useWorkflowEditorLayout from '../../hooks/useWorkflowEditorLayout';
import {
  buildProofEditorLayoutFlags,
} from '../../utils/workflowBuilderAddAction';
import {
  BRANCH_CARD_MIN_WIDTH,
  computeBranchLaneGlobalCenterX,
  isConditionStep,
  normalizeConditionConfig,
  shouldRenderMainChainEndNode,
} from '../../utils/workflowConditionUtils';
import {
  createBranchAddTarget,
  createMainAddTarget,
  duplicateBranchStepAfter,
  findBranchStepLocation,
  getBranchStepsFromConfig,
  insertCatalogActionAtTarget,
  removeBranchStepFromCondition,
  updateBranchStepInCondition,
} from '../../utils/workflowConditionBranchSteps';
import {
  getStepDisplayLabel,
  getWorkflowStepDescription,
} from '../../utils/workflowBuilderUtils';
import {
  getWorkflowActionLaneLayout,
  isSameTriggerConnectorGeometry,
} from '../../utils/workflowTriggerConnectorLayout';
import { duplicateStepAfter } from '../../utils/workflowStepReorder';
import { resolveWorkflowStepTypeConfig } from '../../utils/workflowStepTypeConfig';
import {
  buildInteractiveAddActionDebug,
  captureWorkflowProofGeometry,
  compareWorkflowProofGeometry,
} from '../../utils/workflowProofGeometry';

const INITIAL_WORKFLOW = {
  triggers: [
    { id: 'trigger-1', name: 'Trigger 1', trigger_type: 'manual', config: {} },
    { id: 'trigger-2', name: 'Trigger 2', trigger_type: 'manual', config: {} },
    { id: 'trigger-3', name: 'Trigger 3', trigger_type: 'manual', config: {} },
  ],
  steps: [
    { id: 'step-sms', type: 'send_sms', name: 'Send SMS', label: 'Send SMS' },
    { id: 'step-email', type: 'send_email', name: 'Send Email', label: 'Send Email' },
  ],
};

function ProofStepNode({
  step,
  onRemove,
  onEdit,
  isMoving,
  isSelected,
  onDragHandleStart,
  onContextMenuAction,
  hasClipboard,
  readOnly = false,
  columnWidth,
  isBranchStep = false,
}) {
  const stepConfig = resolveWorkflowStepTypeConfig(step);
  const displayLabel = getStepDisplayLabel(step) || stepConfig.label;

  return (
    <WorkflowStepCard
      step={step}
      stepConfig={stepConfig}
      title={displayLabel}
      typeLabel={stepConfig.label}
      description={getWorkflowStepDescription(step, stepConfig)}
      requiresBackend={Boolean(step.config?.requires_backend)}
      isMoving={isMoving}
      isSelected={isSelected}
      readOnly={readOnly}
      hasClipboard={hasClipboard}
      onEdit={onEdit}
      onRemove={onRemove}
      onDragHandleStart={onDragHandleStart}
      onContextMenuAction={onContextMenuAction}
      columnWidth={columnWidth}
      isBranchStep={isBranchStep}
    />
  );
}

function ProofStepEditorPanel({
  step,
  onSave,
  onClose,
  width,
  overlayMode,
  activePanelType,
}) {
  if (!step) return null;

  return (
    <WorkflowEditorPanelShell
      isOpen
      width={width}
      overlayMode={overlayMode}
      activePanelType={activePanelType}
      className="bg-zinc-900 border-l border-zinc-700"
      testId={`proof-editor-${activePanelType}`}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="border-b border-zinc-700 px-4 py-3">
          <h3 className="text-sm font-semibold text-white">Edit {step.label || step.name || step.type}</h3>
          <p className="mt-1 text-xs text-zinc-500">Proof harness editor — same drawer shell as production builder</p>
        </div>
        <div className="flex-1 overflow-y-auto p-4 text-sm text-zinc-300">
          Step type: <span className="font-mono text-emerald-400">{step.type}</span>
        </div>
        <div className="flex gap-2 border-t border-zinc-700 p-4">
          <button
            type="button"
            className="rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white"
            data-testid="proof-editor-save"
            onClick={onSave}
          >
            Save
          </button>
          <button
            type="button"
            className="rounded-md border border-zinc-600 px-3 py-2 text-sm text-zinc-300"
            data-testid="proof-editor-close"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </WorkflowEditorPanelShell>
  );
}

function resolveActivePanelType(step) {
  if (!step) return 'actions';
  const type = String(step.type || '').toLowerCase();
  if (type === 'send_email' || type === 'email_action') return 'email';
  if (type === 'send_sms' || type === 'sms') return 'sms';
  if (type === 'wait') return 'wait';
  if (type === 'if_else' || type === 'condition') return 'condition';
  if (type === 'send_notification' || type === 'internal_notification') return 'notification';
  if (type === 'backend_action') return 'backend';
  return 'step';
}

const InteractiveAddActionProofHarness = () => {
  const containerRef = useRef(null);
  const baselineRef = useRef(null);
  const existingActionCountRef = useRef(0);
  const existingTriggerCountRef = useRef(0);
  const triggerConnectorLayoutRef = useRef(null);
  const pendingAddTargetRef = useRef(null);

  const [workflow, setWorkflow] = useState(INITIAL_WORKFLOW);
  const [triggerConnectorLayout, setTriggerConnectorLayout] = useState(null);
  const [pendingAddTarget, setPendingAddTarget] = useState(null);
  const [insertedActionTarget, setInsertedActionTarget] = useState(null);
  const [expectedBranchCenterX, setExpectedBranchCenterX] = useState(null);
  const [editingBranchContext, setEditingBranchContext] = useState(null);
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [editorVisible, setEditorVisible] = useState(false);
  const [phase, setPhase] = useState('initial');
  const [operation, setOperation] = useState('initial');
  const [selectedPlusIndex, setSelectedPlusIndex] = useState(null);
  const [addedActionType, setAddedActionType] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [insertedStepId, setInsertedStepId] = useState(null);
  const [duplicatedStepId, setDuplicatedStepId] = useState(null);
  const [debug, setDebug] = useState(null);

  const handleTriggerConnectorLayoutChange = useCallback((layout) => {
    if (isSameTriggerConnectorGeometry(triggerConnectorLayoutRef.current, layout)) {
      return;
    }
    triggerConnectorLayoutRef.current = layout;
    setTriggerConnectorLayout(layout);
  }, []);

  const actionLaneLayout = useMemo(
    () => getWorkflowActionLaneLayout(triggerConnectorLayout),
    [triggerConnectorLayout],
  );

  const editorLayoutFlags = useMemo(
    () => buildProofEditorLayoutFlags({
      showActionsPanel,
      editingStep,
      editorVisible,
    }),
    [editorVisible, editingStep, showActionsPanel],
  );

  const {
    editorPanelWidth,
    editorOverlayMode,
  } = useWorkflowEditorLayout(editorLayoutFlags);

  const closeEditorPanels = useCallback(() => {
    setShowActionsPanel(false);
    setEditorVisible(false);
    setEditingStep(null);
    setEditingBranchContext(null);
    pendingAddTargetRef.current = null;
    setPendingAddTarget(null);
  }, []);

  const assignPendingAddTarget = useCallback((target) => {
    pendingAddTargetRef.current = target;
    setPendingAddTarget(target);
  }, []);

  const handleAddStep = useCallback((index) => {
    closeEditorPanels();
    setSelectedPlusIndex(index);
    assignPendingAddTarget(createMainAddTarget(index));
    setShowActionsPanel(true);
    setPhase('add-drawer-open');
    setOperation('add-drawer-open');
  }, [assignPendingAddTarget, closeEditorPanels]);

  const handleAddBranchStep = useCallback((conditionStepId, branchId, insertIndex, parentPath = []) => {
    closeEditorPanels();
    const topConditionStep = workflow.steps.find((step) => step.id === conditionStepId);
    let conditionStep = topConditionStep;
    if (parentPath?.length) {
      let branchSteps = workflow.steps;
      for (const segment of parentPath) {
        const parent = branchSteps.find((item) => item.id === segment.conditionStepId);
        if (!parent || !isConditionStep(parent)) break;
        const config = normalizeConditionConfig(parent.config);
        branchSteps = getBranchStepsFromConfig(config, segment.branchId);
      }
      conditionStep = branchSteps.find((item) => item.id === conditionStepId) || topConditionStep;
    }
    if (conditionStep && isConditionStep(conditionStep)) {
      setExpectedBranchCenterX(computeBranchLaneGlobalCenterX({
        steps: workflow.steps,
        conditionStepId,
        branchId,
        parentPath,
        mainGlobalCenterX: actionLaneLayout.globalCenterX,
        getBranchSteps: getBranchStepsFromConfig,
      }));
    }
    assignPendingAddTarget(createBranchAddTarget({
      conditionStepId,
      branchId,
      insertIndex,
      parentPath,
    }));
    setShowActionsPanel(true);
    setPhase('add-drawer-open');
    setOperation('add-drawer-open');
  }, [actionLaneLayout.globalCenterX, assignPendingAddTarget, closeEditorPanels, workflow.steps]);

  const handleSelectAction = useCallback((action) => {
    const target = pendingAddTargetRef.current;
    const existingActionCount = workflow.steps.length;
    const result = insertCatalogActionAtTarget(workflow.steps, target, action);

    existingActionCountRef.current = existingActionCount;
    setWorkflow((previous) => ({ ...previous, steps: result.newSteps }));
    setShowActionsPanel(false);
    setInsertedActionTarget(result.insertedTarget ?? null);
    setAddedActionType(action.id);
    setActionType(action.id);
    setInsertedStepId(result.primaryStep?.id ?? null);
    setEditingStep(result.primaryStep);
    setEditingBranchContext(result.insertedTarget?.type === 'branch' ? {
      conditionStepId: result.insertedTarget.conditionStepId,
      branchId: result.insertedTarget.branchId,
    } : null);
    setEditorVisible(true);
    pendingAddTargetRef.current = null;
    setPendingAddTarget(null);
    setPhase('action-inserted');
    setOperation('action-inserted');
    window.requestAnimationFrame(() => {
      setPhase('editor-open');
      setOperation('editor-open');
    });
  }, [workflow.steps]);

  const handleSaveEditor = useCallback(() => {
    setPhase('saved');
    setOperation('saved');
    setEditorVisible(false);
    setEditingStep(null);
    setEditingBranchContext(null);
    window.requestAnimationFrame(() => {
      setPhase('editor-closed');
    });
  }, []);

  const handleCloseEditor = useCallback(() => {
    setEditorVisible(false);
    setEditingStep(null);
    setEditingBranchContext(null);
    setPhase('editor-closed');
  }, []);

  const handleRemoveStep = useCallback((stepId) => {
    const branchLocation = findBranchStepLocation(workflow.steps, stepId);
    setWorkflow((previous) => ({
      ...previous,
      steps: branchLocation
        ? removeBranchStepFromCondition(previous.steps, branchLocation, stepId)
        : previous.steps.filter((step) => step.id !== stepId),
    }));
    setPhase('deleted');
    setOperation('action-deleted');
    if (editingStep?.id === stepId) {
      setEditingStep(null);
      setEditingBranchContext(null);
      setEditorVisible(false);
    }
  }, [editingStep?.id, workflow.steps]);

  const openStepEditor = useCallback((step) => {
    const branchLocation = findBranchStepLocation(workflow.steps, step?.id);
    setShowActionsPanel(false);
    setEditingStep(step);
    setEditingBranchContext(branchLocation ? {
      conditionStepId: branchLocation.conditionStepId,
      branchId: branchLocation.branchId,
    } : null);
    setEditorVisible(true);
    setPhase('editor-open');
    setOperation('card-edited');
    setActionType(step?.type ?? null);
  }, [workflow.steps]);

  const handleBranchStepContextAction = useCallback((action, stepId, branchContext) => {
    if (action === 'delete') {
      handleRemoveStep(stepId);
      return;
    }
    if (action === 'copy' && branchContext) {
      const { steps: nextSteps, duplicatedStep } = duplicateBranchStepAfter(
        workflow.steps,
        branchContext,
        stepId,
      );
      if (!duplicatedStep) return;
      setWorkflow((previous) => ({ ...previous, steps: nextSteps }));
      setDuplicatedStepId(duplicatedStep.id);
      setPhase('duplicated');
      setOperation('action-duplicated');
    }
  }, [handleRemoveStep, workflow.steps]);

  const handleStepContextAction = useCallback((action, stepId, helpers) => {
    if (action === 'copy') {
      const existingActionCount = workflow.steps.length;
      const { steps: nextSteps, duplicatedStep } = duplicateStepAfter(workflow.steps, stepId);
      if (!duplicatedStep) return;
      existingActionCountRef.current = existingActionCount;
      setWorkflow((previous) => ({ ...previous, steps: nextSteps }));
      setDuplicatedStepId(duplicatedStep.id);
      setPhase('duplicated');
      setOperation('action-duplicated');
      const source = workflow.steps.find((step) => step.id === stepId);
      setActionType(source?.type ?? null);
      return;
    }
    helpers?.onContextMenuAction?.(action, stepId);
  }, [workflow.steps]);

  const handleTriggersChange = useCallback((triggers) => {
    const previousCount = workflow.triggers?.length ?? 0;
    setWorkflow((previous) => ({ ...previous, triggers }));
    if (triggers.length > previousCount) {
      setOperation('trigger-duplicated');
      setPhase('duplicated');
    } else if (triggers.length < previousCount) {
      setOperation('trigger-deleted');
      setPhase('deleted');
    }
  }, [workflow.triggers?.length]);

  const handleReset = useCallback(() => {
    baselineRef.current = null;
    existingActionCountRef.current = 0;
    existingTriggerCountRef.current = 0;
    triggerConnectorLayoutRef.current = null;
    setWorkflow(INITIAL_WORKFLOW);
    setTriggerConnectorLayout(null);
    pendingAddTargetRef.current = null;
    setPendingAddTarget(null);
    setInsertedActionTarget(null);
    setExpectedBranchCenterX(null);
    setEditingBranchContext(null);
    setShowActionsPanel(false);
    setEditingStep(null);
    setEditorVisible(false);
    setPhase('initial');
    setOperation('initial');
    setSelectedPlusIndex(null);
    setAddedActionType(null);
    setActionType(null);
    setInsertedStepId(null);
    setDuplicatedStepId(null);
    setDebug(null);
  }, []);

  const refreshDebug = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const current = captureWorkflowProofGeometry(container);
    if (!current) return;

    if (!baselineRef.current) {
      baselineRef.current = current;
      existingActionCountRef.current = current.actionCenters?.length ?? 0;
      existingTriggerCountRef.current = current.triggerCenters?.length ?? 0;
    }

    const comparison = compareWorkflowProofGeometry(baselineRef.current, current, {
      existingActionCount: existingActionCountRef.current,
      existingTriggerCount: existingTriggerCountRef.current,
    });

    setDebug(buildInteractiveAddActionDebug({
      operation,
      phase,
      actionType,
      duplicatedStepId,
      insertedStepId,
      selectedPlusIndex,
      addedActionType,
      steps: workflow.steps,
      container,
      before: baselineRef.current,
      current,
      comparison,
      pendingAddTarget: pendingAddTargetRef.current ?? pendingAddTarget,
      insertedActionTarget,
      expectedBranchCenterX,
    }));
  }, [
    actionType,
    addedActionType,
    duplicatedStepId,
    expectedBranchCenterX,
    insertedActionTarget,
    insertedStepId,
    operation,
    pendingAddTarget,
    phase,
    selectedPlusIndex,
    workflow.steps,
  ]);

  useLayoutEffect(() => {
    let frame2;
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(refreshDebug);
    });
    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }, [
    refreshDebug,
    workflow.steps.length,
    workflow.triggers?.length,
    showActionsPanel,
    editorVisible,
    editingStep,
    triggerConnectorLayout,
    actionLaneLayout.width,
    actionLaneLayout.centerX,
    phase,
    operation,
  ]);

  const renderEditorPanel = () => {
    if (showActionsPanel) {
      return (
        <WorkflowActionsPanel
          isOpen
          theme="dark"
          advancedMode
          allowAdvancedActions
          width={editorPanelWidth}
          overlayMode={editorOverlayMode}
          onClose={() => {
            setShowActionsPanel(false);
            pendingAddTargetRef.current = null;
            setPendingAddTarget(null);
            setPhase('initial');
            setOperation('initial');
          }}
          onSelectAction={handleSelectAction}
          activePanelType="actions"
        />
      );
    }

    if (editorVisible && editingStep) {
      return (
        <ProofStepEditorPanel
          step={editingStep}
          onSave={handleSaveEditor}
          onClose={handleCloseEditor}
          width={editorPanelWidth}
          overlayMode={editorOverlayMode}
          activePanelType={resolveActivePanelType(editingStep)}
        />
      );
    }

    return null;
  };

  const graphStable = debug
    && !debug.didMainLaneMove
    && !debug.didExistingActionsMoveHorizontally
    && !debug.didConnectorPathsChangeHorizontally
    && !debug.didGraphTransformChange
    && !debug.hasUnexpectedGlobalEndUnderBranches
    && !debug.hasMissingBranchLane
    && !debug.hasFloatingCenterActionUnderBranches
    && debug.didInsertIntoMainLaneByMistake !== true;

  return (
    <div
      ref={containerRef}
      className="mx-auto max-w-[1440px]"
      data-testid="interactive-add-action-proof"
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm text-zinc-400">
          Use real trigger/action card controls: duplicate, delete, stats, menu, plus, and add-action drawer.
        </span>
        <button
          type="button"
          className="rounded-md bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200"
          data-testid="proof-interactive-reset"
          onClick={handleReset}
        >
          Reset scenario
        </button>
        <span
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            graphStable ? 'bg-emerald-600/20 text-emerald-300' : 'bg-rose-600/20 text-rose-300'
          }`}
          data-testid="interactive-proof-stable-badge"
        >
          {graphStable ? 'GRAPH STABLE' : 'GRAPH DRIFTED'}
        </span>
        <span className="text-xs text-zinc-500">Phase: {phase} · Operation: {operation}</span>
      </div>

      <div className="h-[720px] overflow-auto rounded-xl border border-zinc-700">
        <WorkflowBuilderBodyLayout
          variant="platform"
          editorPanel={renderEditorPanel()}
          editorPanelWidth={editorPanelWidth}
          editorOverlayMode={editorOverlayMode}
          onEditorOverlayDismiss={handleCloseEditor}
          workspace={(
            <WorkflowCanvasViewport className="h-full" autoFitOnMount={false}>
              {() => (
                <div className="relative flex w-full flex-col items-center space-y-4 p-6">
                  <WorkflowBuilderMainChain
                    width={actionLaneLayout.width}
                    wrapperWidth={actionLaneLayout.wrapperWidth}
                    centerX={actionLaneLayout.centerX}
                    globalCenterX={actionLaneLayout.globalCenterX}
                  >
                    <WorkflowTriggerCanvasSection
                      workflow={workflow}
                      readOnly={false}
                      workflowId="new"
                      onTriggersChange={handleTriggersChange}
                      onConnectorLayoutChange={handleTriggerConnectorLayoutChange}
                      onStatsOpen={() => {
                        setOperation('stats-opened');
                        setPhase('initial');
                      }}
                      onAddTrigger={() => {}}
                      onEditTrigger={() => {}}
                    />

                    <WorkflowBuilderStepList
                      steps={workflow.steps}
                      theme="dark"
                      readOnly={false}
                      advancedMode
                      columnWidth={actionLaneLayout.width}
                      connectorCenterX={actionLaneLayout.centerX}
                      columnOffset={actionLaneLayout.offset}
                      selectedStepId={editingStep?.id}
                      onStepsChange={(steps) => setWorkflow((previous) => ({ ...previous, steps }))}
                      onAddStep={handleAddStep}
                      onRemove={handleRemoveStep}
                      renderStep={(step, index, helpers) => (
                        <ProofStepNode
                          step={step}
                          onRemove={handleRemoveStep}
                          onEdit={openStepEditor}
                          isMoving={helpers.isMovingStep(step.id)}
                          isSelected={helpers.isSelected(step.id)}
                          onDragHandleStart={helpers.onDragHandleStart}
                          onContextMenuAction={(action, stepId) => {
                            if (action === 'delete') {
                              handleRemoveStep(stepId);
                              return;
                            }
                            if (action === 'copy') {
                              handleStepContextAction(action, stepId, helpers);
                              return;
                            }
                            if (action === 'notes' || action === 'move' || action === 'move-block') {
                              setOperation('action-menu-opened');
                              setPhase('initial');
                            }
                            helpers.onContextMenuAction(action, stepId);
                          }}
                          hasClipboard={helpers.hasClipboard}
                        />
                      )}
                      renderConditionBranch={(step, index, _helpers, branchHelpers) => (
                        isConditionStep(step) ? (
                          <WorkflowConditionBranchCanvas
                            step={step}
                            theme="dark"
                            readOnly={false}
                            selectedBranchStepId={editingBranchContext ? editingStep?.id : null}
                            onGraphHeightChange={branchHelpers?.onGraphHeightChange}
                            onAddStepToBranch={handleAddBranchStep}
                            renderBranchStep={(branchStep, branchContext) => (
                              <ProofStepNode
                                step={branchStep}
                                onRemove={handleRemoveStep}
                                onEdit={openStepEditor}
                                isMoving={false}
                                isSelected={branchContext.isSelected}
                                onDragHandleStart={() => {}}
                                onContextMenuAction={(action, stepId) => (
                                  handleBranchStepContextAction(action, stepId, branchContext)
                                )}
                                hasClipboard={false}
                                columnWidth={BRANCH_CARD_MIN_WIDTH}
                                isBranchStep
                              />
                            )}
                          />
                        ) : null
                      )}
                    />

                    {shouldRenderMainChainEndNode(workflow.steps) ? (
                      <div
                        className="relative"
                        style={{ width: actionLaneLayout.width, marginLeft: actionLaneLayout.offset, height: 48 }}
                        data-workflow-action-lane-end
                      >
                        <div
                          className="absolute rounded-full bg-zinc-800 px-6 py-3 text-sm font-medium text-zinc-400"
                          style={{ left: actionLaneLayout.centerX, transform: 'translateX(-50%)' }}
                        >
                          End of Workflow
                        </div>
                      </div>
                    ) : null}
                  </WorkflowBuilderMainChain>
                </div>
              )}
            </WorkflowCanvasViewport>
          )}
        />
      </div>

      <pre
        className="mt-4 max-h-80 overflow-auto rounded-lg bg-zinc-900 p-4 text-xs text-zinc-300"
        data-testid="interactive-add-action-debug"
      >
        {JSON.stringify(debug, null, 2)}
      </pre>
    </div>
  );
};

export default InteractiveAddActionProofHarness;
